import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { Locale } from '../i18n/resources';
import type { AppTheme } from '../types/theme';
import type {
  ChatbotConversationsPageSize,
  ChatbotTrainingExamplesPageSize,
  DocumentPageSize,
  GalleryPageSize,
  ResolvedUserConfig,
  UserConfig,
} from '../types/userConfig';
import type { UserConfigContextValue } from '../types/userConfigContext';
import {
  clampRagMaxResults,
  clampRagMinScore,
} from '../utils/ragRetrievalSettings';
import { useUpdateUserConfigMutation } from './mutations/useUpdateUserConfigMutation';
import {
  capPlatformChatLastSeenMessageIds,
  upsertPlatformChatLastSeenMessageId,
} from './platformChatLastSeenMessageIds';
import { mergeUserConfig } from './userConfigModel';

type UseUserConfigActionsParams = {
  isAuthenticated: boolean;
  config: ResolvedUserConfig;
  isUserConfigReady: boolean;
  setConfig: Dispatch<SetStateAction<ResolvedUserConfig>>;
};

export function useUserConfigActions({
  isAuthenticated,
  config,
  isUserConfigReady,
  setConfig,
}: UseUserConfigActionsParams): {
  value: UserConfigContextValue;
  updateConfigMutation: ReturnType<typeof useUpdateUserConfigMutation>;
} {
  const updateConfigMutation = useUpdateUserConfigMutation();
  // useMutation() return identity changes every render — only `mutate` is stable.
  const updateConfigMutate = updateConfigMutation.mutate;

  const updateConfig = useCallback(
    (partial: UserConfig, options?: { persist?: boolean }) => {
      setConfig((current) => mergeUserConfig(current, partial));

      if (options?.persist !== false && isAuthenticated) {
        updateConfigMutate(partial);
      }
    },
    [isAuthenticated, setConfig, updateConfigMutate],
  );

  const setTheme = useCallback(
    (theme: AppTheme) => {
      updateConfig({ theme });
    },
    [updateConfig],
  );

  const setLocale = useCallback(
    (locale: Locale) => {
      updateConfig({ locale });
    },
    [updateConfig],
  );

  const setKnowledgeDocumentsPageSize = useCallback(
    (knowledgeDocumentsPageSize: DocumentPageSize) => {
      updateConfig({ knowledgeDocumentsPageSize });
    },
    [updateConfig],
  );

  const setGalleryImagesPageSize = useCallback(
    (galleryImagesPageSize: GalleryPageSize) => {
      updateConfig({ galleryImagesPageSize });
    },
    [updateConfig],
  );

  const setChatbotConversationsPageSize = useCallback(
    (chatbotConversationsPageSize: ChatbotConversationsPageSize) => {
      updateConfig({ chatbotConversationsPageSize });
    },
    [updateConfig],
  );

  const setChatbotTrainingExamplesPageSize = useCallback(
    (chatbotTrainingExamplesPageSize: ChatbotTrainingExamplesPageSize) => {
      updateConfig({ chatbotTrainingExamplesPageSize });
    },
    [updateConfig],
  );

  const setPinnedKnowledgeDatabaseIds = useCallback(
    (pinnedKnowledgeDatabaseIds: string[]) => {
      updateConfig({ pinnedKnowledgeDatabaseIds });
    },
    [updateConfig],
  );

  const setReplacePlatformChatDraftTextOnTranscription = useCallback(
    (replacePlatformChatDraftTextOnTranscription: boolean) => {
      updateConfig({ replacePlatformChatDraftTextOnTranscription });
    },
    [updateConfig],
  );

  const setKeepSidebarOpen = useCallback(
    (keepSidebarOpen: boolean) => {
      updateConfig({ keepSidebarOpen });
    },
    [updateConfig],
  );

  const setPlatformChatMaxResults = useCallback(
    (platformChatMaxResults: number) => {
      updateConfig({
        platformChatMaxResults: clampRagMaxResults(platformChatMaxResults),
      });
    },
    [updateConfig],
  );

  const setPlatformChatMinScore = useCallback(
    (platformChatMinScore: number) => {
      updateConfig({
        platformChatMinScore: clampRagMinScore(platformChatMinScore),
      });
    },
    [updateConfig],
  );

  const setPlatformChatRagSettings = useCallback(
    (values: { maxResults: number; minScore: number }) => {
      updateConfig({
        platformChatMaxResults: clampRagMaxResults(values.maxResults),
        platformChatMinScore: clampRagMinScore(values.minScore),
      });
    },
    [updateConfig],
  );

  const markPlatformChatSessionSeen = useCallback(
    (sessionId: string, messageId: string, options?: { persist?: boolean }) => {
      if (sessionId.length === 0 || messageId.length === 0) {
        return;
      }

      const shouldPersist = options?.persist === true;

      setConfig((current) => {
        const platformChatLastSeenMessageIds =
          upsertPlatformChatLastSeenMessageId(
            current.platformChatLastSeenMessageIds,
            sessionId,
            messageId,
          );

        const unchanged =
          platformChatLastSeenMessageIds ===
          current.platformChatLastSeenMessageIds;

        // Persist even when local state already matches — viewing updates are
        // local-only, and leave must still write the map to the backend.
        if (shouldPersist && isAuthenticated) {
          updateConfigMutate({
            platformChatLastSeenMessageIds: unchanged
              ? current.platformChatLastSeenMessageIds
              : platformChatLastSeenMessageIds,
          });
        }

        if (unchanged) {
          return current;
        }

        return mergeUserConfig(current, { platformChatLastSeenMessageIds });
      });
    },
    [isAuthenticated, setConfig, updateConfigMutate],
  );

  const seedPlatformChatLastSeenMessageIds = useCallback(
    (seeds: Record<string, string>) => {
      setConfig((current) => {
        let changed = false;
        const next: Record<string, string> = {
          ...current.platformChatLastSeenMessageIds,
        };

        for (const [sessionId, messageId] of Object.entries(seeds)) {
          if (
            sessionId.length === 0 ||
            messageId.length === 0 ||
            next[sessionId] != null
          ) {
            continue;
          }
          next[sessionId] = messageId;
          changed = true;
        }

        if (!changed) {
          return current;
        }

        const platformChatLastSeenMessageIds =
          capPlatformChatLastSeenMessageIds(next);

        if (isAuthenticated) {
          updateConfigMutate({ platformChatLastSeenMessageIds });
        }

        return mergeUserConfig(current, { platformChatLastSeenMessageIds });
      });
    },
    [isAuthenticated, setConfig, updateConfigMutate],
  );

  const value = useMemo<UserConfigContextValue>(
    () => ({
      isUserConfigReady,
      theme: config.theme,
      setTheme,
      locale: config.locale,
      setLocale,
      knowledgeDocumentsPageSize: config.knowledgeDocumentsPageSize,
      setKnowledgeDocumentsPageSize,
      galleryImagesPageSize: config.galleryImagesPageSize,
      setGalleryImagesPageSize,
      chatbotConversationsPageSize: config.chatbotConversationsPageSize,
      setChatbotConversationsPageSize,
      chatbotTrainingExamplesPageSize: config.chatbotTrainingExamplesPageSize,
      setChatbotTrainingExamplesPageSize,
      pinnedKnowledgeDatabaseIds: config.pinnedKnowledgeDatabaseIds,
      setPinnedKnowledgeDatabaseIds,
      replacePlatformChatDraftTextOnTranscription:
        config.replacePlatformChatDraftTextOnTranscription,
      setReplacePlatformChatDraftTextOnTranscription,
      keepSidebarOpen: config.keepSidebarOpen,
      setKeepSidebarOpen,
      platformChatLastSeenMessageIds: config.platformChatLastSeenMessageIds,
      markPlatformChatSessionSeen,
      seedPlatformChatLastSeenMessageIds,
      platformChatMaxResults: config.platformChatMaxResults,
      setPlatformChatMaxResults,
      platformChatMinScore: config.platformChatMinScore,
      setPlatformChatMinScore,
      setPlatformChatRagSettings,
    }),
    [
      isUserConfigReady,
      config,
      setTheme,
      setLocale,
      setKnowledgeDocumentsPageSize,
      setGalleryImagesPageSize,
      setChatbotConversationsPageSize,
      setChatbotTrainingExamplesPageSize,
      setPinnedKnowledgeDatabaseIds,
      setReplacePlatformChatDraftTextOnTranscription,
      setKeepSidebarOpen,
      markPlatformChatSessionSeen,
      seedPlatformChatLastSeenMessageIds,
      setPlatformChatMaxResults,
      setPlatformChatMinScore,
      setPlatformChatRagSettings,
    ],
  );

  return { value, updateConfigMutation };
}
